// src/observability/ErrorBoundary.tsx
// Root React error boundary that reports render-time errors to the reporter.
// Duplicated per frontend repo (see ems-api docs/observability/phase-3-frontend-reporter.md).
import { Component, type ErrorInfo, type ReactNode } from 'react';
import { captureException } from './errorReporter';
import { isChunkLoadError, reloadForStaleChunk } from '@/utils/chunk-reload';

interface ErrorBoundaryProps {
    children: ReactNode;
    fallback?: ReactNode;
}

interface ErrorBoundaryState {
    hasError: boolean;
    isChunkError: boolean;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
    state: ErrorBoundaryState = { hasError: false, isChunkError: false };

    static getDerivedStateFromError(error: Error): ErrorBoundaryState {
        return { hasError: true, isChunkError: isChunkLoadError(error) };
    }

    componentDidCatch(error: Error, info: ErrorInfo): void {
        captureException(error, { kind: 'react', componentStack: info.componentStack ?? undefined });
        // A stale lazy-chunk after a deploy is a transient load failure, not a crash —
        // recover by reloading to the current build (guarded against loops). captureException
        // uses keepalive, so the report still reaches the backend before the reload.
        if (isChunkLoadError(error)) {
            reloadForStaleChunk();
        }
    }

    render(): ReactNode {
        if (this.state.hasError) {
            // A chunk-error reload is already in flight; render nothing so the crash UI
            // does not flash in the moment before the page navigates.
            if (this.state.isChunkError) {
                return null;
            }
            return this.props.fallback ?? <div style={{ padding: 24 }}>Something went wrong.</div>;
        }
        return this.props.children;
    }
}
